import { useCallback, useRef, useState } from 'react';
import CatalogService from '@clickersphere/core/services/CatalogService';
import { detectImageMime } from '../../utils/detectImageMime';

interface IUploadImageResult {
  fileKey: string;
}

export function useUploadImage() {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const uploadAsync = useCallback(
    async (file: File): Promise<IUploadImageResult> => {
      setProgress(0);
      setIsUploading(true);
      setError(null);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        if (!file.type) {
          throw new Error('File has no MIME type');
        }

        const detectedMime = await detectImageMime(file);
        if (!detectedMime || detectedMime !== file.type) {
          throw new Error('invalid_content');
        }

        const { uploadUrl, fileKey } = await CatalogService.getUploadUrl({
          contentType: file.type,
          fileSize: file.size,
        });

        await CatalogService.uploadFileWithProgress(
          uploadUrl,
          file,
          (percent) => setProgress(percent),
          controller.signal,
        );

        return { fileKey };
      } catch (err) {
        const uploadError =
          err instanceof Error ? err : new Error('Upload failed');
        setError(uploadError);
        throw uploadError;
      } finally {
        setIsUploading(false);
        abortControllerRef.current = null;
      }
    },
    [],
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    uploadAsync,
    progress,
    isUploading,
    abort,
    error,
  };
}
